import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { downloadXlsx } from '../utils/exportXlsx';
import ReportsModal from '../components/ReportsModal';

// 811 Ticket Finder — Ezee Fiber locate tickets found nightly on WA 811, plotted from
// the "811 Ticket Finder" ArcGIS layer. Tickets are only ever added; progress is
// tracked in ArcGIS on a field the locate team owns.

const inputStyle = { backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' };
const GOLD = '#c9a84c';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
const isExpired = (t) => t.expires && new Date(t.expires) < new Date();
const cityWaiting = (t) => /not yet responded/i.test(t.city_status || '');
// 811 Status colour: amber while the city owes a response, green once answered.
const cityColor = (t) => (!t.city_status ? '#6b7280' : cityWaiting(t) ? '#fbbf24' : t.city_status === 'City not notified' ? '#9ca3af' : '#4ade80');

export default function TicketFinder() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tickets, setTickets] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('all');
  const [excavator, setExcavator] = useState('all');
  const [showExpired, setShowExpired] = useState(true);
  const [waitingOnly, setWaitingOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [starting, setStarting] = useState(false);
  const [mapMsg, setMapMsg] = useState('Loading map…');
  const [showReports, setShowReports] = useState(false);

  const mapRef = useRef(null);
  const viewRef = useRef(null);
  const layerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [{ data: t }, { data: s }] = await Promise.all([api.get('/tickets'), api.get('/tickets/status')]);
      setTickets(t);
      setStatus(s);
      setError('');
    } catch {
      setError('Could not load tickets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // While a search runs, refresh every 10s so new tickets show up as they land.
  useEffect(() => {
    if (!status?.running) return undefined;
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [status?.running, load]);

  // The City filter is the city we locate for (a Lakewood ticket with STEIL01 is Steilacoom's).
  const cities = useMemo(() => [...new Set(tickets.map((t) => t.locate_for || t.city).filter(Boolean))].sort(), [tickets]);
  const excavators = useMemo(() => [...new Set(tickets.map((t) => t.excavator).filter(Boolean))].sort(), [tickets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (city !== 'all' && (t.locate_for || t.city) !== city) return false;
      if (excavator !== 'all' && t.excavator !== excavator) return false;
      if (!showExpired && isExpired(t)) return false;
      if (waitingOnly && !cityWaiting(t)) return false;
      if (!q) return true;
      return [t.ticket_no, t.street, t.cross_street, t.address, t.location, t.remarks, t.contact_name, t.response_summary, t.city_status]
        .some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [tickets, search, city, excavator, showExpired, waitingOnly]);

  // ---- map ----------------------------------------------------------------
  // The ArcGIS layer is the source of truth for the points (and shows the team's
  // progress field in its popup). Dig-area polygons come from our own copy.
  useEffect(() => {
    if (!status || viewRef.current) return undefined;
    let view;
    let cancelled = false;
    (async () => {
      try {
        const { data: tok } = await api.get('/arcgis/portal-token');
        const [
          { default: esriConfig }, { default: esriId }, { default: EsriMap }, { default: MapView },
          { default: FeatureLayer }, { default: GraphicsLayer }, { default: Graphic },
        ] = await Promise.all([
          import('@arcgis/core/config'),
          import('@arcgis/core/identity/IdentityManager'),
          import('@arcgis/core/Map'),
          import('@arcgis/core/views/MapView'),
          import('@arcgis/core/layers/FeatureLayer'),
          import('@arcgis/core/layers/GraphicsLayer'),
          import('@arcgis/core/Graphic'),
        ]);
        if (cancelled) return;
        esriConfig.portalUrl = tok.orgUrl;
        esriId.registerToken({ server: `${tok.orgUrl}/sharing/rest`, token: tok.token, ssl: true });

        const map = new EsriMap({ basemap: 'dark-gray-vector' });
        view = new MapView({ container: mapRef.current, map, center: [-122.42, 47.24], zoom: 11 });
        viewRef.current = view;

        const polys = new GraphicsLayer({ title: 'Dig areas' });
        map.add(polys);
        layerRef.current = { polys, Graphic };

        if (status.layerUrl) {
          const svcRoot = status.layerUrl.split('/rest/')[0] + '/rest/services';
          esriId.registerToken({ server: svcRoot, token: tok.token, ssl: true });
          const fl = new FeatureLayer({ url: status.layerUrl, outFields: ['*'], title: '811 Tickets' });
          map.add(fl);
          await fl.when();
          fl.popupTemplate = fl.createPopupTemplate();
          fl.popupTemplate.title = 'Ticket {ticket_no}';
          layerRef.current.fl = fl;
          setMapMsg('');
        } else {
          setMapMsg('The ArcGIS layer is created on the first search — showing tickets from the app until then.');
        }
        await view.when();
      } catch (e) {
        console.error(e);
        if (!cancelled) setMapMsg('Could not load the map.');
      }
    })();
    return () => { cancelled = true; };
  }, [status]);

  useEffect(() => () => { viewRef.current?.destroy(); viewRef.current = null; }, []);

  // Redraw dig areas (and fallback points when there's no ArcGIS layer yet) for the filtered list.
  useEffect(() => {
    const ref = layerRef.current;
    if (!ref?.polys) return;
    const { polys, Graphic, fl } = ref;
    polys.removeAll();
    for (const t of filtered) {
      for (const ring of t.polygons || []) {
        if (ring.length < 3) continue;
        polys.add(new Graphic({
          geometry: { type: 'polygon', rings: [ring.map((p) => [p.lng, p.lat])], spatialReference: { wkid: 4326 } },
          symbol: { type: 'simple-fill', color: [201, 168, 76, 0.12], outline: { color: [201, 168, 76, 0.9], width: 1.5 } },
        }));
      }
      if (!fl && t.latitude != null) {
        polys.add(new Graphic({
          geometry: { type: 'point', longitude: t.longitude, latitude: t.latitude },
          symbol: { type: 'simple-marker', color: [201, 168, 76], size: 9, outline: { color: [13, 27, 46], width: 1.2 } },
          attributes: { ticket_no: t.ticket_no },
          popupTemplate: { title: `Ticket ${t.ticket_no}`, content: `${t.excavator || ''}<br/>${[t.address, t.street].filter(Boolean).join(' ')} · ${t.city || ''}${t.locate_for && t.locate_for !== t.city ? ` (for ${t.locate_for})` : ''}<br/>${t.location || ''}` },
        }));
      }
    }
    // Keep the ArcGIS points in step with the filters.
    if (fl) {
      const ids = filtered.map((t) => t.ticket_no).filter((n) => /^\d+$/.test(n));
      fl.definitionExpression = filtered.length === tickets.length ? null : (ids.length ? `ticket_no IN (${ids.map((n) => `'${n}'`).join(',')})` : '1=0');
    }
  }, [filtered, tickets.length, mapMsg]);

  function zoomTo(t) {
    const view = viewRef.current;
    if (!view || t.latitude == null) return;
    view.goTo({ center: [t.longitude, t.latitude], zoom: 18 });
    mapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // ---- actions ------------------------------------------------------------

  async function runNow() {
    setStarting(true);
    try {
      await api.post('/tickets/run');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not start a search.');
    } finally {
      setStarting(false);
    }
  }

  // Billing export straight from the ArcGIS layer, so it carries the progress field
  // (and anything else the team added there).
  async function exportExcel() {
    setExporting(true);
    try {
      const { data } = await api.get('/tickets/export');
      if (!data.rows.length) { setError('The ArcGIS layer has no tickets yet.'); return; }
      const fields = data.fields.filter((f) => f.name !== 'OBJECTID' && f.name !== 'Shape__Area' && f.name !== 'Shape__Length');
      await downloadXlsx({
        filename: `811-tickets-${new Date().toISOString().slice(0, 10)}.xlsx`,
        sheetName: '811 Tickets',
        headers: fields.map((f) => f.alias),
        rows: data.rows.map((r) => fields.map((f) => {
          const v = r[f.name];
          if (v == null) return '';
          return f.type === 'esriFieldTypeDate' ? new Date(v).toLocaleString('en-US') : v;
        })),
      });
    } catch (e) {
      setError(e.response?.data?.error || 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  const lastRun = status?.runs?.find((r) => r.finished_at);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-black text-white tracking-widest uppercase">811 Ticket Finder</h1>
            <p className="text-gray-400 text-sm mt-1">
              Ezee Fiber locate tickets in {status?.cities?.map((c) => c[0] + c.slice(1).toLowerCase()).join(', ') || '…'} · all contractors
            </p>
          </div>
          <div className="text-xs text-gray-400 text-right space-y-0.5">
            {status?.running ? (
              <p style={{ color: GOLD }}>Searching WA 811 now…</p>
            ) : lastRun ? (
              <p>
                Last search {fmtDateTime(lastRun.finished_at)} · {lastRun.added} new
                {lastRun.ok ? '' : <span className="text-red-400"> · finished with errors</span>}
              </p>
            ) : (
              <p>No searches yet</p>
            )}
            {status?.nextRun && <p>Next search {fmtDateTime(status.nextRun)}</p>}
            {status?.itemUrl && (
              <a href={status.itemUrl} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: GOLD }}>Open layer in ArcGIS ↗</a>
            )}
          </div>
        </div>

        {lastRun?.ok && lastRun.warning && (
          <p className="text-xs text-yellow-300 mb-3">⚠ {lastRun.warning}</p>
        )}
        {isAdmin && lastRun && !lastRun.ok && lastRun.error && (
          <pre className="text-xs text-red-300 bg-red-950/30 border border-red-900 rounded p-2 mb-3 whitespace-pre-wrap max-h-32 overflow-auto">{lastRun.error}</pre>
        )}
        {isAdmin && status?.counts?.unpushed > 0 && !status.running && (
          <p className="text-xs text-yellow-300 mb-3">{status.counts.unpushed} ticket{status.counts.unpushed === 1 ? '' : 's'} not in ArcGIS yet — they'll be added on the next search.</p>
        )}

        <div className="relative rounded-xl border overflow-hidden mb-5" style={{ borderColor: '#1e3a5f', height: 460 }}>
          <div ref={mapRef} className="w-full h-full" />
          {mapMsg && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 text-xs text-gray-300 px-3 py-1.5 rounded" style={{ backgroundColor: 'rgba(8,15,26,0.85)' }}>
              {mapMsg}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <select value={city} onChange={(e) => setCity(e.target.value)} className="text-sm rounded px-3 py-2 border text-white focus:outline-none" style={inputStyle}>
            <option value="all">All cities</option>
            {cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={excavator} onChange={(e) => setExcavator(e.target.value)} className="text-sm rounded px-3 py-2 border text-white focus:outline-none" style={inputStyle}>
            <option value="all">All contractors</option>
            {excavators.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ticket #, street, location…"
            className="flex-1 min-w-[200px] text-sm rounded px-3 py-2 border text-white placeholder-gray-500 focus:outline-none"
            style={inputStyle}
          />
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none">
            <input type="checkbox" checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)} className="w-3.5 h-3.5 accent-yellow-500" />
            Show expired
          </label>
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none" title="Tickets where the city hasn't responded in WA 811 yet">
            <input type="checkbox" checked={waitingOnly} onChange={(e) => setWaitingOnly(e.target.checked)} className="w-3.5 h-3.5 accent-yellow-500" />
            Waiting on city only
          </label>
          <span className="text-xs text-gray-500">{filtered.length} ticket{filtered.length === 1 ? '' : 's'}</span>
          <button
            onClick={exportExcel}
            disabled={exporting || !status?.layerUrl}
            className="text-xs px-3 py-2 rounded font-bold uppercase tracking-wide transition disabled:opacity-50"
            style={{ backgroundColor: GOLD, color: '#0d1b2e' }}
            title="Every ticket in the ArcGIS layer, with all its fields, for billing"
          >
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </button>
          <button
            onClick={() => setShowReports(true)}
            className="text-xs px-3 py-2 rounded border font-bold uppercase tracking-wide transition hover:opacity-80"
            style={{ borderColor: GOLD, color: GOLD }}
            title="Daily Esri completions, billing (city completions), or no-response report"
          >
            Reports
          </button>
          {isAdmin && (
            <button
              onClick={runNow}
              disabled={starting || status?.running}
              className="text-xs px-3 py-2 rounded border font-bold uppercase tracking-wide text-gray-300 hover:text-white transition disabled:opacity-50"
              style={{ borderColor: '#1e3a5f' }}
              title="Search WA 811 now instead of waiting for midnight"
            >
              {status?.running ? 'Searching…' : 'Search now'}
            </button>
          )}
        </div>

        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

        {loading ? (
          <p className="text-gray-500 text-sm">Loading tickets…</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-500 text-sm">
            {tickets.length === 0 ? 'No tickets yet — the first search runs tonight at midnight.' : 'No tickets match the current filters.'}
          </p>
        ) : (
          <div className="rounded-xl border overflow-x-auto" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
            <table className="w-full text-sm">
              <thead className="border-b" style={{ backgroundColor: '#080f1a', borderColor: '#1e3a5f' }}>
                <tr>
                  {['Ticket #', 'Called', 'Expires', 'Contractor', 'City', 'Street', '811 Status', 'WA 811 Response status', 'Location of Work', ''].map((h, i) => (
                    <th key={i} className="text-left px-3 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#1e3a5f' }}>
                {filtered.map((t) => (
                  <tr key={t.id} className="transition hover:bg-white/5 align-top">
                    <td className="px-3 py-3 text-xs font-bold whitespace-nowrap" style={{ color: GOLD }}>
                      {t.ticket_link ? <a href={t.ticket_link} target="_blank" rel="noreferrer" className="hover:underline">{t.ticket_no}</a> : t.ticket_no}
                    </td>
                    <td className="px-3 py-3 text-gray-300 text-xs whitespace-nowrap">{fmtDate(t.call_date)}</td>
                    <td className={`px-3 py-3 text-xs whitespace-nowrap ${isExpired(t) ? 'text-gray-600 line-through' : 'text-gray-300'}`}>{fmtDate(t.expires)}</td>
                    <td className="px-3 py-3 text-white text-xs">{t.excavator || '—'}</td>
                    <td className="px-3 py-3 text-gray-300 text-xs">
                      {t.locate_for || t.city || '—'}
                      {t.locate_for && t.city && t.locate_for !== t.city && <div className="text-gray-500">in {t.city}</div>}
                    </td>
                    <td className="px-3 py-3 text-gray-300 text-xs">
                      {[t.address, t.street].filter(Boolean).join(' ') || '—'}
                      {t.cross_street && <span className="text-gray-500"> @ {t.cross_street}</span>}
                    </td>
                    <td className="px-3 py-3 text-xs font-semibold max-w-[12rem]" style={{ color: cityColor(t) }}>
                      {t.city_status || 'Not checked yet'}
                    </td>
                    <td
                      className="px-3 py-3 text-xs max-w-[14rem]"
                      style={{ color: !t.response_summary ? '#6b7280' : t.response_summary === 'All responded' ? '#4ade80' : '#fbbf24' }}
                      title={(t.responses || []).map((r) => `${r.name}: ${r.status || 'Not yet responded'}`).join('\n') || undefined}
                    >
                      {t.response_summary || 'Not checked yet'}
                    </td>
                    <td className="px-3 py-3 text-gray-400 text-xs max-w-md" title={[t.location, t.remarks].filter(Boolean).join('\n\n')}>
                      <span className="line-clamp-2">{t.location || '—'}</span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap">
                      {t.latitude != null ? (
                        <button onClick={() => zoomTo(t)} className="text-xs hover:underline" style={{ color: GOLD }}>Map</button>
                      ) : (
                        <span className="text-xs text-gray-600" title="The ticket has no dig area, so it can't be plotted">No location</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showReports && <ReportsModal onClose={() => setShowReports(false)} />}
    </Layout>
  );
}
